import React from "react";
import {
  Box,
  Typography,
  Container,
  Link,
  List,
  ListItem,
  ListItemText,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState, useRef, useEffect } from "react";

const sections = [
  { id: "what-we-collect", label: "Information We Collect" },
  { id: "use-of-info", label: "How We Use Your Information" },
  { id: "share-info", label: " How We Share Your Information" },
  { id: "cookies", label: "Cookies and Tracking Technologies" },
  { id: "data-security", label: "Data Security" },
  { id: "data-retention", label: "Data Retention" },
  { id: "policy-rights", label: "Your Privacy Rights" },
  { id: "third-party", label: "Third-Party Links" },
  { id: "chindren-policy", label: "Children's Privacy" },
  { id: "change-policy", label: "Changes to This Privacy Policy" },
  { id: "contact-us", label: "Contact Us" },
];


const PrivacyPolicy = () => {

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const MAXWIDTH = useMediaQuery(theme.breakpoints.between("lg, xl"));
  const is4K = useMediaQuery("(min-width: 3840px)");

  const isLaptop = useMediaQuery(theme.breakpoints.up("lg")); // same as used in Header
  const isTabMobile = useMediaQuery(theme.breakpoints.down("md")); // used for sidebar condition

  const [activeSection, setActiveSection] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      let current = "";
      sections.forEach((section) => {
        const element = document.getElementById(section.id);
        if (element && window.scrollY >= element.offsetTop - 110) {
          current = section.id;
        }
      });
      setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections]);

  const [activeTop, setActiveTop] = useState(0);
  const itemRefs = useRef([]);

  useEffect(() => {
    const index = sections.findIndex((s) => s.id === activeSection);
    if (index !== -1 && itemRefs.current[index]) {
      const el = itemRefs.current[index];
      const offset = el.offsetTop + el.offsetHeight / 2 - 15; // center of item - half of blue line
      setActiveTop(offset);
    }
  }, [activeSection]);

  const SidebarList = (
    <Box sx={{ position: "relative", pl: 4, py: 2 }}>
      {/* Light full vertical line */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 16,
          bottom: 0,
          width: "2px",
          backgroundColor: "#e0e0e0",
        }}
      >
        {/* Active blue line inside the light vertical line */}
        <Box
          sx={{
            position: "absolute",
            top: `${activeTop}px`,
            left: 0,
            width: "2px",
            height: "55px",
            backgroundColor: "#0085B1",
            transition: "top 0.3s ease",
          }}
        />
      </Box>

      <List dense disablePadding>
        {sections.map((section, index) => {
          const isActive = activeSection === section.id;

          return (
            <Box
              key={section.id}
              mb={1}
              sx={{ position: "relative" }}
              ref={(el) => (itemRefs.current[index] = el)}
            >
              <ListItem
                disablePadding
                onClick={() => setDrawerOpen(false)}
                sx={{
                  borderRadius: 1,
                  pl: 2,
                  pr: 1,
                  py: 0.5,
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                }}
              >
                <Link
                  href={`#${section.id}`}
                  underline="none"
                  sx={{
                    color: isActive ? "#0085B1" : "#000000DE",
                    fontSize: "18px",
                    fontWeight: isActive ? 600 : 300,
                    width: "100%",
                    display: "block",
                    "&:hover": {
                      color: "#0085B1",
                    },
                  }}
                >
                  <ListItemText
                    primary={section.label}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: "0.95rem",
                      },
                    }}
                  />
                </Link>
              </ListItem>
            </Box>
          );
        })}
      </List>
    </Box>
  );

  const typostyle = {
    color: "#000000DE",
    fontWeight: 300,
    fontSize: "18px",
  };
  return (
    <Box
      sx={{
        width: "100%",
        height: "auto",
        pl: {
          xs: 2, // 16px
          sm: 3, // 24px
          md: is4K ? "200px" : "180px", // match header padding
        },
        pr: {
          xs: 2,
          sm: 3,
          md: is4K ? "200px" : "180px", // match header padding
        },
        mt: "120px", // margin from top to offset the sticky header
        mb: "120px", // bottom margin for spacing
      }}
    >
      <Container disableGutters>
        <Grid container spacing={4}>
          {/* Table of Contents */}
          {/* Sidebar for Desktop */}
          {!isTabMobile && (
            <Grid item xs={12} md={4}>
              <Box sx={{ position: "sticky", top: 100 }}>{SidebarList}</Box>
            </Grid>
          )}

          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Typography
              variant="body1"
              sx={{ color: "#000000DE", fontWeight: 300, fontSize: "18px" }}
              gutterBottom
            >
              At DevAngles Software Pvt. Ltd., we are committed to protecting
              the privacy of our website visitors and users of our software
              products and services. This Privacy Policy outlines the types of
              information we collect, how we use it, and the measures we take to
              safeguard your data.
            </Typography>

            <Typography
              variant="body1"
              sx={{ color: "#000000DE", fontWeight: 300, fontSize: "18px" }}
              gutterBottom
            >
              By accessing or using our website and services, you agree to the
              terms of this Privacy Policy. If you do not agree with these
              terms, please do not use our website or services.
            </Typography>

            <Box id="what-we-collect" pt={6}>
              <Typography variant="h6" gutterBottom>
                1. Information We Collect
              </Typography>

              <Typography variant="body1" sx={typostyle} gutterBottom>
                We may collect various types of information, including:
              </Typography>

              <List>
                {/* 1. Personal Information */}
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary="a. Personal Information:"
                    secondary={
                      <List sx={{ pl: 3, ...typostyle }}>
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          •{" "}
                          <strong style={{ fontWeight: 500 }}>
                            Contact Information:
                          </strong>{" "}
                          Name, email address, phone number, company name, and
                          postal address when you fill out forms, subscribe to
                          newsletters, request a demo, or contact us.
                        </ListItem>
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          •{" "}
                          <strong style={{ fontWeight: 500 }}>
                            Account Information:
                          </strong>{" "}
                          Usernames, passwords (encrypted), and other
                          credentials used to access our software or services.
                        </ListItem>
                      </List>
                    }
                  />
                </ListItem>

                {/* 2. Usage Data */}
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary="b. Usage Data:"
                    secondary={
                      <List sx={{ pl: 3, ...typostyle }}>
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          •{" "}
                          <strong style={{ fontWeight: 500 }}>
                            Website Usage:
                          </strong>{" "}
                          Information about how you interact with our website,
                          such as IP address, browser type, operating system,
                          referring URLs, pages viewed, time spent on pages, and
                          clickstream data.
                        </ListItem>
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          •{" "}
                          <strong style={{ fontWeight: 500 }}>
                            Software Usage:
                          </strong>{" "}
                          Data on how you use our software, including features
                          accessed, actions performed, error reports, and
                          performance metrics. This data is often anonymized or
                          aggregated.
                        </ListItem>
                      </List>
                    }
                  />
                </ListItem>

                {/* 3. Device Information */}
                <ListItem>
                  <ListItemText
                    primary="c. Device Information:"
                    secondary="Information about the device you use to access our website or software, including device type, unique device identifiers, and mobile network information."
                    secondaryTypographyProps={{
                      sx: { ...typostyle }, pl: 3,
                    }}
                  />
                </ListItem>

                {/* 4. Cookies and Tracking */}
                <ListItem>
                  <ListItemText
                    primary="d. Cookies and Tracking Technologies:"
                    secondary="Information collected through cookies, web beacons, and similar technologies (see Section 4 for more details)."
                    secondaryTypographyProps={{
                      sx: { ...typostyle }, pl: 3,
                    }}
                  />
                </ListItem>
              </List>
            </Box>

            <Box id="use-of-info" pt={6}>
              <Typography variant="h6" gutterBottom>
                2. How We Use Your Information
              </Typography>

              <Typography variant="body2" sx={typostyle} gutterBottom>
                We use the information we collect for various purposes,
                including:
              </Typography>

              <List>
                {/* 1. Personal Information */}
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary="a. To Provide and Maintain Our Services:"
                    secondary={
                      <List
                        sx={{
                          pl: 3,
                          color: "#000000DE",
                          fontWeight: 300,
                          fontSize: "18px",
                        }}
                      >
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          • To operate, maintain, and improve our website,
                          software, and services.
                        </ListItem>
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          • To process your transactions and deliver products or
                          services you have requested.
                        </ListItem>
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          • To provide customer support and respond to your
                          inquiries.
                        </ListItem>
                      </List>
                    }
                  />
                </ListItem>

                {/* 2. Usage Data */}
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary="b. Usage Data:"
                    secondary={
                      <List
                        sx={{
                          pl: 3,
                          color: "#000000DE",
                          fontWeight: 300,
                          fontSize: "18px",
                        }}
                      >
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          • To send you transactional emails (e.g., account
                          notifications, purchase confirmations).
                        </ListItem>
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          • To send you marketing and promotional communications
                          about our products, services, offers, and news that
                          may be of interest to you (you can opt out at any
                          time).
                        </ListItem>
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          • To provide important updates and announcements
                          related to our services.
                        </ListItem>
                      </List>
                    }
                  />
                </ListItem>

                {/* 3. Device Information */}
                <ListItem>
                  <ListItemText
                    primary="c. For Analytics and Improvement:"
                    secondary={
                      <List sx={{ pl: 3, ...typostyle }}>
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          • To understand how users interact with our website
                          and software to enhance user experience.
                        </ListItem>
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          • To monitor and analyze trends, usage, and activities
                          in connection with our services.
                        </ListItem>
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          • To detect, prevent, and address technical issues,
                          fraud, or security vulnerabilities.
                        </ListItem>
                      </List>
                    }
                  />
                </ListItem>

                {/* 4. Cookies and Tracking */}
                <ListItem>
                  <ListItemText
                    primary="d. For Legal and Security Reasons:"
                    secondary={
                      <List sx={{ pl: 3, ...typostyle }}>
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          • To comply with legal obligations and regulatory
                          requirements.
                        </ListItem>
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          • To protect our rights, privacy, safety, or property,
                          and that of our users or the public.
                        </ListItem>
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          • To enforce our terms and conditions.
                        </ListItem>
                      </List>
                    }
                  />
                </ListItem>
              </List>
            </Box>

            {/* Section:  How We Share Your Information */}

            <Box id="share-info" pt={6}>
              <Typography variant="h6" gutterBottom>
                3. How We Share Your Information
              </Typography>
              <Typography variant="body2" sx={{ ...typostyle }} paragraph>
                We do not sell, rent, or trade your personal information to
                third parties. We may share your information in the following
                circumstances:
              </Typography>
              <ListItem>
                <ListItemText
                  primary="a. With Service Providers:"
                  secondary="We may share information with third-party vendors, consultants, and other service providers who perform services on our behalf, such as payment processing, website hosting, data analysis, customer support, marketing assistance, and IT services. These service providers are obligated to protect your information and use it only for the purposes for which we disclose it to them."
                  secondaryTypographyProps={{
                    sx: { ...typostyle }, pl: 3,
                  }}
                />
              </ListItem>

              {/* 4. Cookies and Tracking */}
              <ListItem>
                <ListItemText
                  primary="b. For Legal Reasons:"
                  secondary="We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court order or government agency)."
                  secondaryTypographyProps={{
                    sx: { ...typostyle }, pl: 3,
                  }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="c. Business Transfers:"
                  secondary="In the event of a merger, acquisition, reorganization, bankruptcy, or sale of all or a portion of our assets, your information may be transferred as part of that transaction. We will notify you via email and/or a prominent notice on our website of any such change in ownership or control of your personal information."
                  secondaryTypographyProps={{
                    sx: { ...typostyle }, pl: 3,
                  }}
                />
              </ListItem>

              {/* 4. With Your Consent */}
              <ListItem>
                <ListItemText
                  primary="d. With Your Consent:"
                  secondary="We may share your information with third parties when we have your explicit consent to do so."
                  secondaryTypographyProps={{
                    sx: { ...typostyle }, pl: 3,
                  }}
                />
              </ListItem>
              {/* 5. Aggregated or Anonymized Data */}
              <ListItem>
                <ListItemText
                  primary="e. Aggregated or Anonymized Data:"
                  secondary="We may share aggregated or anonymized data that cannot reasonably be used to identify you, with third parties for various purposes, including analytics, research, or marketing."
                  secondaryTypographyProps={{
                    sx: { ...typostyle }, pl: 3,
                  }}
                />
              </ListItem>
            </Box>

            {/* Section:  Cookies and Tracking Technologies */}

            <Box id="cookies" pt={6}>
              <Typography variant="h6" gutterBottom>
                4. Cookies and Tracking Technologies
              </Typography>

              <Typography variant="body2" sx={{ ...typostyle }} gutterBottom>
                We use cookies and similar tracking technologies (like web
                beacons and pixels) to track activity on our website and hold
                certain information.
              </Typography>

              <List>
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary="a. Cookies:"
                    secondary=" Small data files stored on your device that help us remember you and your preferences, analyze website usage, and deliver targeted advertising."
                    secondaryTypographyProps={{
                      sx: { ...typostyle }, pl:3
                    }}
                  />
                </ListItem>

                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary="b. Types of Cookies We Use:"
                    secondary={
                      <List sx={{ pl: 3, ...typostyle }}>
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          •{" "}
                          <strong style={{ fontWeight: 500 }}>
                            Essential Cookies:
                          </strong>{" "}
                          Necessary for the website to function properly.
                        </ListItem>
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          •{" "}
                          <strong style={{ fontWeight: 500 }}>
                            Performance/Analytics Cookies:
                          </strong>{" "}
                          Help us understand how visitors interact with our
                          website.
                        </ListItem>
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          •{" "}
                          <strong style={{ fontWeight: 500 }}>
                            Functionality Cookies:
                          </strong>{" "}
                          Remember your choices and preferences.
                        </ListItem>
                        <ListItem sx={{ display: "list-item", pl: 2 }}>
                          •{" "}
                          <strong style={{ fontWeight: 500 }}>
                            Advertising/Targeting Cookies:
                          </strong>{" "}
                          Used to deliver relevant advertisements.
                        </ListItem>
                      </List>
                    }
                  />
                </ListItem>
              </List>
              <Typography variant="body2" sx={{ ...typostyle }} gutterBottom>
                You can instruct your browser to refuse all cookies or to
                indicate when a cookie is being sent. However, if you do not
                accept cookies, you may not be able to use some portions of our
                website or services.
              </Typography>
            </Box>

            {/* Section:   Data Security */}
            <Box id="data-security" pt={6}>
              <Typography variant="h6" gutterBottom>
                5. Data Security
              </Typography>
              <Typography variant="body2" sx={{ ...typostyle }} paragraph>
                We implement reasonable technical and organizational measures
                designed to protect your personal information from unauthorized
                access, use, alteration, or destruction. However, no method of
                transmission over the Internet or method of electronic storage
                is 100% secure. Therefore, while we strive to use commercially
                acceptable means to protect your personal information, we cannot
                guarantee its absolute security.
              </Typography>
            </Box>

            {/* Section:  Data Retention */}
            <Box id="data-retention" pt={6}>
              <Typography variant="h6" gutterBottom>
               6. Data Retention
              </Typography>
              <Typography variant="body2" sx={{ ...typostyle }} paragraph>
                We retain your personal information for as long as necessary to
                fulfill the purposes outlined in this Privacy Policy, unless a
                longer retention period is required or permitted by law (e.g.,
                for legal, tax, or accounting purposes).
              </Typography>
            </Box>

            {/* Section:   Your Privacy Rights */}
            <Box id="policy-rights" pt={6}>
              <Typography variant="h6" gutterBottom>
                7. Your Privacy Rights
              </Typography>
              <Typography variant="body2" sx={{ ...typostyle }} paragraph>
                Depending on your jurisdiction, you may have certain rights
                regarding your personal information, including:
              </Typography>
              <ListItem>
                <ListItemText
                  primary="a. Access:"
                  secondary="The right to request access to the personal information we hold about you."
                  secondaryTypographyProps={{
                    sx: { ...typostyle }, pl: 3,
                  }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="b. Correction/Rectification:"
                  secondary="The right to request that we correct any inaccurate or incomplete personal information."
                  secondaryTypographyProps={{
                    sx: { ...typostyle }, pl: 3,
                  }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="c. Deletion/Erasure:"
                  secondary=" The right to request the deletion of your personal information under certain circumstances."
                  secondaryTypographyProps={{
                    sx: { ...typostyle }, pl: 3,
                  }}
                />
              </ListItem>
              <ListItem >
                <ListItemText
                  primary="d. Objection/Restriction:"
                  secondary="   The right to object to or request the restriction of the processing of your personal information."
                  secondaryTypographyProps={{
                    sx: { ...typostyle }, pl: 3,
                  }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="e. Data Portability:"
                  secondary="The right to receive your personal information in a structured, commonly used, and machine-readable format."
                  secondaryTypographyProps={{
                    sx: { ...typostyle }, pl: 3,
                  }}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="f. Withdraw Consent:"
                  secondary="Where we rely on your consent to process your personal information, you have the right to withdraw that consent at any time."
                  secondaryTypographyProps={{
                    sx: { ...typostyle }, pl: 3,
                  }}
                />
              </ListItem>

              <Typography sx={{ ...typostyle }}>
                To exercise any of these rights, please contact us using the
                contact information provided in section 11. We may require you
                to verify your identity before responding to your request.
              </Typography>
            </Box>

            {/* Section: Third-Party Links */}
            <Box id="third-party" pt={6}>
              <Typography variant="h6" gutterBottom>
                8. Third-Party Links
              </Typography>
              <Typography variant="body2" sx={{ ...typostyle }} paragraph>
                Our website may contain links to third-party websites that are
                not operated by us. We have no control over and assume no
                responsibility for the content, privacy policies, or practices
                of any third-party sites or services. We encourage you to review
                the privacy policies of any third-party websites you visit.
              </Typography>
            </Box>

            {/* Section:  Children's Privacy */}
            <Box id="chindren-policy" pt={6}>
              <Typography variant="h6" gutterBottom>
                9. Children's Privacy
              </Typography>
              <Typography variant="body2" sx={{ ...typostyle }} paragraph>
                Our website and services are not intended for individuals under
                the age of 13. We do not knowingly collect personal information
                from children under 13. If you are a parent or guardian and you
                believe your child has provided us with personal information,
                please contact us, and we will take steps to remove that
                information from our systems.
              </Typography>
            </Box>

            {/* Section: Changes to This Privacy Policy */}
            <Box id="change-policy" pt={6}>
              <Typography variant="h6" gutterBottom>
                10. Changes to This Privacy Policy
              </Typography>
              <Typography variant="body2" sx={{ ...typostyle }} paragraph>
                We may update our Privacy Policy from time to time. We will
                notify you of any changes by posting the new Privacy Policy on
                this page and updating the "Last Updated" date at the top. We
                encourage you to review this Privacy Policy periodically for any
                changes. Your continued use of our website or services after any
                modifications constitutes your acknowledgment of the
                modifications and your agreement to abide and be bound by the
                modified Privacy Policy.
              </Typography>
            </Box>

            {/* Section: Contact Us */}
            <Box id="contact-us" pt={6}>
              <Typography variant="h6" gutterBottom>
                11. Contact Us
              </Typography>
              <Typography variant="body2" sx={{ ...typostyle }} paragraph>
                If you have any questions or concerns about this Privacy Policy
                or our data practices, please contact us at:{" "}
                <a
                  style={{ color: "#0085B1" }}
                  href="mailto:contact@devangles.com"
                >
                  contact@devangles.com
                </a>
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ ...typostyle }} mt={4}>
              Last Updated: 1st July 2025
            </Typography>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
